import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';

const CATEGORIES = [
  { value: 'dry_grass', label: '🌾 Dry Grass (Kadbi/Bhusa)' },
  { value: 'wet_grass', label: '🌿 Green/Wet Grass' },
  { value: 'medical', label: '💊 Medical & Veterinary' },
  { value: 'salary', label: '👷 Staff Salary' },
  { value: 'infrastructure', label: '🏗️ Infrastructure' },
  { value: 'utilities', label: '💡 Utilities (Water/Electric)' },
  { value: 'other', label: '📦 Other' },
];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function ManageExpenses() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [form, setForm] = useState({ category: 'dry_grass', amount: '', description: '', date: new Date().toISOString().split('T')[0], quantity: '', vendor: '', billNumber: '' });

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams(Object.fromEntries(Object.entries(filter).filter(([, v]) => v)));
      const res = await API.get(`/expenses?${q}`);
      setExpenses(res.data.expenses);
      setSummary(res.data.summary);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchExpenses(); }, [filter]);

  const handleAdd = async () => {
    if (!form.amount) return toast.error('Amount is required');
    try {
      await API.post('/expenses', form);
      toast.success('Expense added');
      setShowForm(false);
      setForm({ category: 'dry_grass', amount: '', description: '', date: new Date().toISOString().split('T')[0], quantity: '', vendor: '', billNumber: '' });
      fetchExpenses();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await API.delete(`/expenses/${id}`);
    fetchExpenses();
  };

  const totalExpense = summary.reduce((s, e) => s + e.total, 0);
  const catLabel = c => CATEGORIES.find(x => x.value === c)?.label || c;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>📋 Expenses</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>+ Add Expense</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select className="form-select" style={{ width: 130 }} value={filter.month} onChange={e => setFilter(p => ({ ...p, month: e.target.value }))}>
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select className="form-select" style={{ width: 110 }} value={filter.year} onChange={e => setFilter(p => ({ ...p, year: e.target.value }))}>
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', fontWeight: 700, color: '#8B4513', fontSize: '1.1rem' }}>
          Month Total: ₹{totalExpense.toLocaleString('en-IN')}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        {summary.map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '12px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: '3px solid #8B4513' }}>
            <div style={{ fontSize: '0.8rem', color: '#888' }}>{catLabel(s._id)}</div>
            <div style={{ fontWeight: 700, color: '#8B4513' }}>₹{s.total?.toLocaleString('en-IN')}</div>
          </div>
        ))}
      </div>

      {/* Add form */}
      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>Add Expense</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select className="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Amount (₹) *</label>
              <input className="form-input" type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="0" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date</label>
              <input className="form-input" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input className="form-input" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))} placeholder="e.g., 100 kg" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Vendor / Supplier</label>
              <input className="form-input" value={form.vendor} onChange={e => setForm(p => ({ ...p, vendor: e.target.value }))} placeholder="Vendor name" />
            </div>
            <div className="form-group">
              <label className="form-label">Bill / Invoice No.</label>
              <input className="form-input" value={form.billNumber} onChange={e => setForm(p => ({ ...p, billNumber: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleAdd}>Save Expense</button>
            <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="loader"><div className="spinner" /></div> : (
        <div className="admin-table">
          <table>
            <thead><tr>
              <th>Date</th><th>Category</th><th>Description</th><th>Quantity</th><th>Vendor</th><th>Amount</th><th>Action</th>
            </tr></thead>
            <tbody>
              {expenses.map(e => (
                <tr key={e._id}>
                  <td>{new Date(e.date).toLocaleDateString('en-IN')}</td>
                  <td>{catLabel(e.category)}</td>
                  <td style={{ maxWidth: 200, fontSize: '0.85rem' }}>{e.description || '—'}</td>
                  <td>{e.quantity || '—'}</td>
                  <td>{e.vendor || '—'}</td>
                  <td style={{ fontWeight: 700, color: '#8B4513' }}>₹{e.amount?.toLocaleString('en-IN')}</td>
                  <td><button onClick={() => handleDelete(e._id)} style={{ color: '#c62828', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>🗑️</button></td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No expenses found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
